<template>
  <form v-if="!loading" @submit.prevent="advance" class="payment-method-form">
    <InputRadio
      :value="paymentMethod"
      :options="options"
      :disabled="disabled"
      identified-by="id"
      named-by="name"
      orientation="horizontal"
      @checked="(newValue) => paymentMethod = newValue"
    />

    <div v-if="paymentMethod === 'credit-card'" class="pb-3">
      <div class="image-container my-4">
        <img
          alt="Cartão de crédito"
          src="/build/images/billing/credit-card.png"
        />
      </div>

      <div class="inputs-container">
        <div class="form-group">
          <label for="card-number">{{ __('Número cartão de crédito') }}</label>
          <Cleave
            id="card-number"
            name="card-number"
            type="text"
            :disabled="disabled"
            :placeholder="anyFieldsFilled ? '' : creditCardPlaceholder.number"
            v-model="creditCardData.number"
            :options="{
              creditCard: true,
            }"
          />
          <span class="text-salesflow-info" v-if="creditCardNumberHasError">
            {{ __('Número de cartão de crédito inválido') }}
          </span>
        </div>
        <div class="form-group">
          <label for="card-name">{{ __('Nome impresso no cartão') }}</label>
          <input
            id="card-name"
            name="card-name"
            type="text"
            :disabled="disabled"
            :placeholder="anyFieldsFilled ? '' : creditCardPlaceholder.name"
            v-model="creditCardData.name"
          />
          <span class="text-salesflow-info" v-if="creditCardNameHasError">
            {{ __('Insira nome como no cartão') }}
          </span>
        </div>
        <div class="inputs-container-horizontal">
          <div>
            <label for="card-validity">{{ __('Validade') }}</label>
            <Cleave
              id="card-validity"
              name="card-validity"
              type="text"
              :disabled="disabled"
              :placeholder="anyFieldsFilled ? '' : creditCardPlaceholder.validity"
              v-model="creditCardData.validity"
              :options="{
                date: true,
                dateMin: minDate,
                delimiter: '/',
                datePattern: ['m', 'y']
              }"
            />
            <span class="text-salesflow-info" v-if="creditCardValidityHasError">
              {{ __('Data de expiração inválida') }}
            </span>
          </div>
          <div>
            <label for="card-security-code">{{ __('Cód. segurança') }}</label>
            <Cleave
              id="card-security-code"
              name="card-security-code"
              type="text"
              min="3"
              :disabled="disabled"
              :placeholder="anyFieldsFilled ? '' : creditCardPlaceholder.securityCode"
              v-model="creditCardData.securityCode"
              :options="{
                numericOnly: true,
                blocks: [4],
              }"
            />
            <span class="text-salesflow-info" v-if="creditCardSecurityCodeHasError">
              {{ __('Código inválido') }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="paymentMethod === 'bankslip'">
      <div class="image-container my-4">
        <img
          alt="Boleto"
          src="/build/images/billing/bankslip.png"
        />
      </div>
    </div>

    <button
      id="advance-payment-method"
      type="submit"
      class="w-100 mt-4 salesflow-btn salesflow-btn-primary"
      :disabled="submitting || disabled"
      v-show="showButton"
    >
      {{ submitting ? __('Carregando') + '...' : __('Avançar') }}
    </button>
  </form>
  <div
    v-else
    class="w-100 p-3 d-flex align-items-center justify-content-center"
  >
    <i class="fa fa-circle-o-notch fa-spin" />
  </div>
</template>

<script>
import dayjs from '../../core/dayjs';
import Cleave from 'vue-cleave-component';

export default {
  components: { Cleave },

  props: {
    showButton: {
      type: Boolean,
      required: true,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: true,
    },
    initialPaymentMethod: {
      type: [String, null],
      default: null,
    },
  },

  updated () {
    this.$emit('updated', this.data);
  },

  data () {
    return {
      paymentMethod: null,
      options: [
        { id: 'credit-card', name: 'Cartão de crédito' },
        { id: 'bankslip', name: 'Boleto' },
      ],
      token: null,
      creditCardData: {
        number: '',
        name: '',
        validity: '',
        securityCode: '',
      },
      creditCardPlaceholder: {
        number: '',
        name: '',
        validity: '',
        securityCode: '',
      },
      bankslipData: null,
      loading: false,
      alreadyExists: false,
    };
  },

  created () {
    if (this.initialPaymentMethod) {
      this.paymentMethod = this.initialPaymentMethod;
      return;
    }

    this.loading = true;

    this.$http.get('/billing/payment-method').then(({ data }) => {
      const { method, details } = data;

      if (method === 'BANKSLIP') {
        this.paymentMethod = 'bankslip';

        return;
      } else if (method === 'CREDIT_CARD') {
        this.paymentMethod = 'credit-card';

        this.creditCardPlaceholder = {
          number: details.display_number.split('-').join(' '),
          name: details.holder_name,
          validity: `${details.month}/${(new String(details.year).slice(-2))}`,
          securityCode: 'XXX',
        };

        this.alreadyExists = true;

        return;
      }

      this.paymentMethod = 'credit-card';
    }).catch(() => {
      this.paymentMethod = 'bankslip';

      Alert.error(this.__('Falha ao buscar dados de pagamento'));
    }).finally(() => {
      this.loading = false;
    });
  },

  computed: {
    data () {
      const validity = dayjs(this.creditCardData.validity, "MMYY");

      const name = this.creditCardData.name ?? '';
      const split = name.split(' ');

      const firstName = split.splice(0, 1)[0];
      const lastName = split.join(' ');

      return {
        method: this.paymentMethod,
        alreadyExists: this.alreadyExists,
        anyFieldsFilled: this.anyFieldsFilled,
        data: this.paymentMethod === 'credit-card' ?
          { ...this.creditCardData, firstName, lastName, validity } : this.bankslipData,
      };
    },

    minDate () {
      return dayjs().startOf('month').format('YY-MM');
    },

    creditCardNumberHasError () {
      if (!this.creditCardData.number) {
        return false;
      }

      if (this.creditCardData.number.length < 6) {
        return true;
      }

      return !Iugu.utils.validateCreditCardNumber(this.creditCardData.number);
    },

    creditCardNameHasError () {
      if (!this.creditCardData.name) {
        return false;
      }

      const split = this.creditCardData.name.split(' ');

      if (split.length <= 1) {
        return true;
      }

      const first = split.splice(0, 1)[0];
      const last = split.join(' ');

      return !(
        first && last
      );
    },

    creditCardValidityHasError () {
      if (!this.creditCardData.validity) {
        return false;
      }

      const validity = dayjs(this.creditCardData.validity, "MMYY");

      return !Iugu.utils.validateExpiration(validity.get('month'), validity.get('year'));
    },

    creditCardSecurityCodeHasError () {
      if (!this.creditCardData.securityCode) {
        return false;
      }

      return this.creditCardData.securityCode.length < 3;
    },

    anyFieldsFilled () {
      const {
        number, name,
        validity, securityCode
      } = this.creditCardData;

      return !!(number || name || validity || securityCode);
    },

    anyFieldsUnfilled () {
      const {
        number, name,
        validity, securityCode
      } = this.creditCardData;

      return !!(!number || !name || !validity || !securityCode);
    },
  },

  methods: {
    advance () {
      if (this.paymentMethod === 'bankslip') {
        return this.$emit('advance', this.data);
      }

      if (!this.anyFieldsFilled && this.alreadyExists) {
        return this.$emit('advance', this.data);
      }

      if (
        this.creditCardNumberHasError
        || this.creditCardNameHasError
        || this.creditCardValidityHasError
        || this.creditCardSecurityCodeHasError
        || this.anyFieldsUnfilled
      ) {
        return Alert.error(this.__('Preencha corretamente todos os campos'));
      }

      this.$emit('advance', this.data);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../sass/variables';
@import '../../../sass/mixins';

.payment-method-form {
  width: 100%;;

  .image-container {
    display: flex;
    justify-content: center;

    img {
      max-width: 100%;
    }
  }

  .inputs-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
  }

  label {
    @include salesflow-text();
    margin-bottom: 0px;
  }

  input {
    width: 100%;
    padding: 0.75rem;
    background: white;
    border: 1px solid $salesflow-outline-gray-medium;
    border-radius: 8px;

    @include salesflow-subtitle($color: $salesflow-font-dark);

    &:focus {
      box-shadow: 0px 0px 0px 4px #0D6EFD40 !important;
      border-color: $salesflow-outline-gray-medium !important;
    }

    &::placeholder {
      @include salesflow-subtitle($color: $salesflow-font-dark);
    }

    &:focus::placeholder {
      color: transparent !important;
    }

    transition: box-shadow 0.25s;
  }

  .form-group {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 0px;
  }

  .inputs-container-horizontal {
    width: 100%;
    display: flex;
    justify-content: space-between;
    gap: 1.5rem;

    div {
      width: 100%;
      display: flex;
      flex-direction: column;
    }
  }

}

</style>
